import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Container from "@/components/base/Container";
import TagPill from "@/components/base/TagPill";
import { articles } from "@/mocks/articles";

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const article = articles.find((entry) => entry.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  useSeo({
    title: article ? `${article.title}-咖位观察局` : "文章未找到-咖位观察局",
    description: article?.excerpt ?? "未找到对应的解读文章，返回深度解读列表查看更多内容。",
    keywords: article ? article.tags.join(",") : "网红明星解读,咖位观察",
    type: "article",
    jsonLd:
      article && {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "BlogPosting",
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { "@type": "Organization", name: article.author },
            articleSection: article.category,
            wordCount: article.sections.reduce(
              (total, section) => total + section.paragraphs.join("").length,
              0,
            ),
            inLanguage: "zh-CN",
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: "深度解读", item: "/articles" },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
        ],
      },
  });

  if (!article) {
    return (
      <>
        <SiteHeader />
        <main className="pt-16 md:pt-20">
          <Container>
            <div className="py-24 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-article-line text-3xl" />
              </span>
              <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这篇文章不见了</h1>
              <p className="mt-3 text-sm text-foreground-600">可能是链接有误，去列表里重新挑一篇吧。</p>
              <Link
                to="/articles"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                返回深度解读
              </Link>
            </div>
          </Container>
        </main>
        <SiteFooter />
      </>
    );
  }

  const related = articles.filter((entry) => entry.slug !== article.slug).slice(0, 3);

  return (
    <>
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <Breadcrumbs
          items={[
            { label: "首页", path: "/" },
            { label: "深度解读", path: "/articles" },
            { label: article.title },
          ]}
        />

        <article className="bg-background-50">
          <Container>
            <header className="mx-auto max-w-3xl py-12 md:py-16">
              <TagPill variant="accent">{article.category}</TagPill>
              <h1 className="mt-5 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-[2.5rem]">
                {article.title}
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">{article.excerpt}</p>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span>{article.author}</span>
                <span className="h-3 w-px bg-background-300" />
                <span>{article.date}</span>
                <span className="h-3 w-px bg-background-300" />
                <span>{article.readTime}</span>
                <span className="h-3 w-px bg-background-300" />
                <span className="inline-flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-eye-line text-sm" />
                  </span>
                  {article.views.toLocaleString()}
                </span>
              </div>
            </header>
          </Container>

          <Container>
            <div className="mx-auto h-64 w-full max-w-5xl overflow-hidden rounded-lg md:h-[420px]">
              <img
                src={article.coverImage}
                alt={`${article.title}配图`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </Container>

          <Container>
            <div className="mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-8">
                {article.sections.map((section, index) => (
                  <section key={section.heading} id={`section-${index + 1}`} className="mb-10 scroll-mt-28">
                    <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph.slice(0, 24)} className="text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                    {section.list ? (
                      <ul className="mt-5 space-y-3 rounded-lg border border-background-200 bg-background-100 p-5">
                        {section.list.map((entry) => (
                          <li key={entry} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                              <i className="ri-check-line text-sm" />
                            </span>
                            {entry}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </section>
                ))}

                <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200 pt-6">
                  {article.tags.map((tag) => (
                    <TagPill key={tag} variant="outline">
                      #{tag}
                    </TagPill>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-28">
                  <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <h2 className="font-heading text-sm font-bold text-foreground-950">本文目录</h2>
                    <nav aria-label="文章目录" className="mt-4">
                      <ul className="space-y-3">
                        {article.sections.map((section, index) => (
                          <li key={section.heading}>
                            <a
                              href={`#section-${index + 1}`}
                              className="flex items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                            >
                              <span className="font-heading text-xs text-primary-500">0{index + 1}</span>
                              {section.heading}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>

                  <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-5">
                    <h2 className="font-heading text-sm font-bold text-foreground-950">想看正面交锋？</h2>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                      把抽象的方法论落到具体的同框场景里，对比榜单里有完整维度打分。
                    </p>
                    <Link
                      to="/compare"
                      className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      前往对比榜单
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-arrow-right-line text-base" />
                      </span>
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </Container>
        </article>

        <section className="mt-16 border-t border-background-200/80 bg-background-100 py-14 md:py-16">
          <Container>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {related.map((entry) => (
                <Link
                  key={entry.slug}
                  to={`/articles/${entry.slug}`}
                  className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                >
                  <span className="text-xs font-medium text-primary-600">{entry.category}</span>
                  <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {entry.title}
                  </h3>
                  <p className="mt-3 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    {entry.excerpt}
                  </p>
                  <span className="mt-4 text-xs text-foreground-500">{entry.readTime}</span>
                </Link>
              ))}
            </div>
          </Container>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}